const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000/api';
const TOKEN_KEY = 'ai_mapper_token';

interface TokenPayload {
    sub?: string;
    exp?: number;
    uid?: number | null;
    admin?: boolean;
    scope?: string;
    name?: string;
}

export function getToken(): string | null {
    return localStorage.getItem(TOKEN_KEY);
}

/** Decode the JWT payload — no signature verification, the server validates on every request. */
function decodeToken(): TokenPayload | null {
    const token = getToken();
    if (!token) return null;
    try {
        return JSON.parse(atob(token.split('.')[1]));
    } catch {
        return null;
    }
}

export function isAuthenticated(): boolean {
    const payload = decodeToken();
    if (!payload?.exp) return false;
    return payload.exp * 1000 > Date.now();
}

/** Reads the logged-in username from the JWT `sub` claim. */
export function getUsername(): string | null {
    const payload = decodeToken();
    return typeof payload?.sub === 'string' ? payload.sub : null;
}

/** True for the bootstrap admin — the only principal that sees the User Management page. */
export function getIsAdmin(): boolean {
    return decodeToken()?.admin === true;
}

/** 'all' or a specific ghl_id. UI-only convenience — the server enforces this on every request. */
export function getPracticeScope(): string {
    return decodeToken()?.scope ?? 'all';
}

/**
 * True for the bootstrap admin and for invited users granted every practice
 * (`scope === 'all'`). Single-practice accounts do not get full-access ops tools
 * such as Sync Tracking.
 */
export function hasFullPracticeAccess(): boolean {
    return getIsAdmin() || getPracticeScope() === 'all';
}

/**
 * The reviewing user's display name, straight from the JWT `name` claim
 * (server sets it to "Admin" for the bootstrap admin, else the invited
 * user's `name` or their username if unset) — used for the instant
 * optimistic "Approved by …" update right after an approve/reject, so it
 * matches what a refetch would show instead of falling back to the raw
 * username. Falls back to that same username-based heuristic only for a
 * token minted before this claim existed.
 */
export function getCurrentReviewerLabel(): string {
    const name = decodeToken()?.name;
    if (typeof name === 'string' && name) return name;
    return getIsAdmin() ? 'Admin' : (getUsername() ?? 'You');
}

export async function login(username: string, password: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/auth/token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({ username, password }),
    });

    if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.detail || 'Invalid username or password');
    }

    const { access_token } = await response.json();
    localStorage.setItem(TOKEN_KEY, access_token);
}

export function logout(): void {
    localStorage.removeItem(TOKEN_KEY);
    window.location.reload();
}
